import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {User} from '../../models/user.interface';
import {KfRegistrationService} from '../../services/kf-registration.service';
import {AuthService} from '../../../core/services/auth.service';
import {FormBuilder, FormControl} from '@angular/forms';
import {Master} from '../../../admin/models/master.interface';
import {KfRegistrationDetail} from '../../models/kf-registration-detail.interface';
import {Quantitative} from '../../models/quantitative.interface';
import {MessageService} from '../../../shared/services/message.service';
import {MatDialog} from '@angular/material/dialog';
import {QuestionDialogComponent} from '../../../shared/components/question-dialog/question-dialog.component';
import {KfRegistrationSource} from '../../models/kf-registration-source.interface';
import {Currency} from '../../models/currency.interface';

const resetTitle = 'Reset';
const resetText = `Are you sure you want to reset the registration?<br><br>
                   In case you have entered data on this page already, please be aware that changing this field will result in loss of data.<br>
                   Click continue to confirm, cancel if you do not want to change the field.<br><br> `;

const completedTitle = 'Mark as completed';
const completedText = 'Are you sure you want to mark the registration as complete?<br><br>' +
  'Any modifications will be saved before proceeding. When the registration is marked as completed you won\'t be able to change the values anymore.<br><br>';
const confidentialTitle = 'Mark as confidential';
const confidentialText = 'Are you sure you want to mark the registration as confidential?<br><br>' +
  'You won\'t be able to report data for this registration anymore.<br><br>';
const warningTitle = 'Warning';
const warningCountryText = 'Some total values of this country registration are lower than the equivalent ones of the system registrations, please correct the values, and save again.';
const warningWeeeSystemText = 'Some total values of this system registration are higher than the equivalent ones of the Country registrations, please correct the values, and save again.';

@Component({
  selector: 'app-kf-registration-edit',
  templateUrl: './kf-registration-edit.component.html',
  styleUrls: ['./kf-registration-edit.component.scss']
})
export class KfRegistrationEditComponent implements OnInit {

  currencies!: Currency[];
  keyFigure!: string;
  keyFigureCode!: string;
  originalDetailLevel!: string;
  year!: number;
  user!: User;
  country!: boolean;
  categories: Master[] = [];
  registrationSources!: KfRegistrationSource[];
  kfRegistrationDetail!: KfRegistrationDetail;
  existsPreviousYear = false;
  newKfRegistrationDetail: KfRegistrationDetail = {
    categories: [
      {
        kind: 0, piecesValue: 0, tonsValue: 0, clearingHouse: 0, collection: 0, communication: 0, rd: 0, logistics: 0, office: 0, other: 0,
        treatment: 0, remark: '', operationsRemark: '', overheadRemark: '',
        piecesDifferentValueAllowed: false, tonsDifferentValueAllowed: false, collectionDifferentValueAllowed: false,
        logisticsDifferentValueAllowed: false, treatmentDifferentValueAllowed: false, clearingHouseDifferentValueAllowed: false,
        communicationDifferentValueAllowed: false, rdDifferentValueAllowed: false, officeDifferentValueAllowed: false,
        otherDifferentValueAllowed: false,
        mr: 0, ru: 0, er: 0, td: 0, ld: 0, loss: 0, omr: 0,
        mrDifferentValueAllowed: false, ruDifferentValueAllowed: false, erDifferentValueAllowed: false, tdDifferentValueAllowed: false,
        ldDifferentValueAllowed: false, lossDifferentValueAllowed: false, omrDifferentValueAllowed: false
      }
    ],
    kfRegistration: {
      year: 0,
      detailStatusLevel: '',
      detailLevel: '',
      completionStatus: false,
      validationStatus: false,
      noDataReport: false,
      commentAdminUser: '',
    },
    deviation: 0,
  };

  b2bIncludes = [
    'WEEE from non- household sources (e.g., hospital)',
    'WEEE from manufacturers (e.g., stocks, recalls etc.)',
    'WEEE from other sources (small businesses)',
    'Some WEEE with a financial scheme different from household WEEE',
    'All WEEE with a financial scheme different from household WEEE'
  ];

  generalInfoForm = this.formBuilder.group({
    estimatedDetermined: [''],
    b2bb2c: [''],
    b2bIncludes: [],
    source: [''],
    confidential: [false],
    clearedWeee: [false],
    notes: [''],
  });

  simpleUser = true;

  get estimatedDeterminedControl(): FormControl {
    return this.generalInfoForm.get('estimatedDetermined') as FormControl;
  }

  get b2bb2cControl(): FormControl {
    return this.generalInfoForm.get('b2bb2c') as FormControl;
  }

  get b2bIncludesControl(): FormControl {
    return this.generalInfoForm.get('b2bIncludes') as FormControl;
  }

  get sourceControl(): FormControl {
    return this.generalInfoForm.get('source') as FormControl;
  }

  get confidentialControl(): FormControl {
    return this.generalInfoForm.get('confidential') as FormControl;
  }

  get clearedWeeeControl(): FormControl {
    return this.generalInfoForm.get('clearedWeee') as FormControl;
  }

  get notesControl(): FormControl {
    return this.generalInfoForm.get('notes') as FormControl;
  }

  constructor(private readonly route: ActivatedRoute,
              private readonly kfRegistrationService: KfRegistrationService,
              private readonly authService: AuthService,
              readonly router: Router,
              private readonly formBuilder: FormBuilder,
              private readonly messageService: MessageService,
              private dialog: MatDialog) {
    this.country = this.router.url.includes('country');
  }

  ngOnInit(): void {
    this.simpleUser = this.authService.userHasAnyRole(['SIMPLE_USER']);
    this.route.params.subscribe(
      urlParams => {
        if (urlParams.keyFigure === 'putonmarket') {
          this.keyFigure = 'Put On Market';
        } else if (urlParams.keyFigure === 'collected') {
          this.keyFigure = 'Collected';
        } else if (urlParams.keyFigure === 'cost') {
          this.keyFigure = 'Cost';
          this.kfRegistrationService.getAllCurrency().subscribe(currencies => this.currencies = currencies);
        } else {
          this.keyFigure = 'Recycling';
        }
        this.keyFigureCode = urlParams.keyFigure;
        this.year = urlParams.year;

        this.loadData();
      }
    );

    this.kfRegistrationService.getRegistrationSources().subscribe(
      data => this.registrationSources = data
    );
    if (this.simpleUser) {
      this.generalInfoForm.disable();
    }
  }

  private loadData(): void {
    this.kfRegistrationService.getUser(this.authService.getEmail()).subscribe(
      userData => {
        this.user = userData;
        this.kfRegistrationService.existsPreviousYear(this.keyFigureCode, this.year,
          this.country ? userData.weeeSystem.country.id : userData.weeeSystem.id,
          this.country ? 'country' : 'system').subscribe(response => {
          this.existsPreviousYear = response;
        });

        this.kfRegistrationService
          .getKfRegistration(this.keyFigureCode, this.year,
            this.country ? userData.weeeSystem.country.id : userData.weeeSystem.id,
            this.country ? 'country' : 'system')
          .subscribe(
            registrationData => {

              // si esta not started comprobamos el valor de detail level para cost y collected
              if (!registrationData.kfRegistration) {
                this.checkDetailLevel(userData);
              }

              if (registrationData.kfRegistration && registrationData.categories && registrationData.categories.length > 0) {
                this.kfRegistrationDetail = registrationData;
                this.kfRegistrationDetail.categories.forEach(category => {
                  if (category.keyFiguresCategory) {
                    this.categories?.push(category.keyFiguresCategory);
                  }
                });

                // establecer valores guardados
                this.estimatedDeterminedControl.setValue(this.kfRegistrationDetail.kfRegistration.detailStatusLevel);
                this.b2bb2cControl.setValue(this.kfRegistrationDetail.kfRegistration.detailLevel);
                this.originalDetailLevel = this.kfRegistrationDetail.kfRegistration.detailLevel;
                //this.b2bb2cControl.disable();
                if (this.kfRegistrationDetail.kfRegistration.b2bIncludes) {
                  this.b2bIncludesControl.setValue(JSON.parse(this.kfRegistrationDetail.kfRegistration.b2bIncludes));
                }
                const sourceIndex = this.registrationSources.findIndex(
                  source => source.id === this.kfRegistrationDetail.kfRegistration.registrationSource?.id);
                this.sourceControl.setValue(this.registrationSources[sourceIndex]);
                this.confidentialControl.setValue(this.kfRegistrationDetail.kfRegistration.noDataReport);
                this.clearedWeeeControl.setValue(this.kfRegistrationDetail.kfRegistration.clearedWeee);
                this.notesControl.setValue(this.kfRegistrationDetail.kfRegistration.commentAdminUser);

                // deshabilitar inputs si esta completado el kfRegistration
                if (this.kfRegistrationDetail?.kfRegistration?.completionStatus) {
                  this.estimatedDeterminedControl.disable();
                  this.b2bb2cControl.disable();
                  this.sourceControl.disable();
                  this.b2bIncludesControl.disable();
                  this.confidentialControl.disable();
                  this.clearedWeeeControl.disable();
                  this.notesControl.disable();
                }
              } else {
                this.newKfRegistrationDetail.lastYearDetailLevel = registrationData.lastYearDetailLevel;
                this.newKfRegistrationDetail.lastYearCategories = registrationData.lastYearCategories;
                if (this.country) {
                  this.newKfRegistrationDetail.kfRegistration.country = this.user.weeeSystem.country;
                } else {
                  this.newKfRegistrationDetail.kfRegistration.weeeSystem = this.user.weeeSystem;
                }
                this.newKfRegistrationDetail.deviation = registrationData.deviation;
                if (this.router.url.includes('country')) {
                  this.kfRegistrationService
                    .getCategories('country', this.keyFigureCode, this.year, this.user.weeeSystem.country.id).subscribe(
                    data4 => {
                      this.categories = data4;
                    }
                  );
                } else {
                  this.kfRegistrationService
                    .getCategories('weeesystem', this.keyFigureCode, this.year, this.user.weeeSystem.id).subscribe(
                    data4 => {
                      this.categories = data4;
                    }
                  );

                }

                // valores por defecto
                this.estimatedDeterminedControl.setValue('ESTIMATION');
                this.b2bb2cControl.setValue('DIFFERENTIATED');
                this.sourceControl.setValue(this.registrationSources[0]);
                if(registrationData.kfRegistration) {
                  this.newKfRegistrationDetail.kfRegistration.noDataReport = registrationData.kfRegistration.noDataReport;
                  this.confidentialControl.setValue(registrationData.kfRegistration.noDataReport);
                }
              }
            }
          );
      }
    );
  }

  private checkDetailLevel(user: User): void {
    if (this.keyFigureCode === 'cost' || this.keyFigureCode === 'collected') {
      this.kfRegistrationService.getKfRegistrationDetailLevel(this.keyFigureCode === 'cost' ? 'collected' : 'cost', this.year,
        this.country ? user.weeeSystem.country.id : user.weeeSystem.id,
        this.country ? 'country' : 'system')
        .subscribe(detailLevel => {
          if (detailLevel.content) {
            this.b2bb2cControl.setValue(detailLevel.content.toUpperCase());
            this.b2bb2cControl.disable();
          }
        });
    }
  }

  markAsCompleted(): void {
    // obtener keyFigure sin espacios y con minúsculas
    const kf = this.keyFigure.replace(/ /g, '').toLowerCase();

    const dialogRef = this.dialog.open(QuestionDialogComponent, {data: {title: completedTitle, text: completedText}, maxWidth: 600});
    dialogRef.componentInstance.confirm.subscribe(() => {
      this.kfRegistrationService.markAsCompleted(
        kf, this.year, this.country ? this.user.weeeSystem.country.id : this.user.weeeSystem.id, this.country ? 'country' : 'system')
        .subscribe(
          () => {
            this.redirectUrl();
          });
      dialogRef.close();
    });
  }

  markAsConfidential(): void {
    // obtener keyFigure sin espacios y con minúsculas
    const kf = this.keyFigure.replace(/ /g, '').toLowerCase();

    const dialogRef = this.dialog.open(QuestionDialogComponent, {data: {title: confidentialTitle, text: confidentialText}, maxWidth: 600});
    dialogRef.componentInstance.confirm.subscribe(() => {
      this.kfRegistrationService.markAsConfidential(
        kf, this.year, this.country ? this.user.weeeSystem.country.id : this.user.weeeSystem.id, this.country ? 'country' : 'system')
        .subscribe(
          () => {
            this.redirectUrl();
          });
      dialogRef.close();
    });
  }

  markAsCompletedWarning(): void {
    this.dialog.open(QuestionDialogComponent, {
      data: {title: warningTitle, text: this.country ? warningCountryText : warningWeeeSystemText}, maxWidth: 600
    });
  }

  reset(): void {
    const dialogRef = this.dialog.open(QuestionDialogComponent, {data: {title: resetTitle, text: resetText}, maxWidth: 600});
    dialogRef.componentInstance.confirm.subscribe(() => {
      this.kfRegistrationService.deleteKfRegistration(this.kfRegistrationDetail, this.router.url.split('/')[4]).subscribe(() => {
        this.redirectUrl();
      });
    });
  }

  generalSubmit(): void {
    if (this.kfRegistrationDetail && this.kfRegistrationDetail.categories.length > 0) { // sobreescribir los datos que había
      this.kfRegistrationDetail.kfRegistration = this.setGeneralValues(this.kfRegistrationDetail.kfRegistration);
    } else { // crear uno nuevo
      this.newKfRegistrationDetail.kfRegistration.year = this.year;
      this.newKfRegistrationDetail.kfRegistration = this.setGeneralValues(this.newKfRegistrationDetail.kfRegistration);
    }
  }

  private setGeneralValues(quantitative: Quantitative): Quantitative {
    quantitative.detailStatusLevel = this.estimatedDeterminedControl.value;
    quantitative.detailLevel = this.b2bb2cControl.value;
    quantitative.b2bIncludes = JSON.stringify(this.b2bIncludesControl.value);
    quantitative.registrationSource = this.sourceControl.value;
    quantitative.noDataReport = this.confidentialControl.value;
    quantitative.commentAdminUser = this.notesControl.value;
    if (this.keyFigure === 'Collected' || this.keyFigure === 'Cost') {
      quantitative.clearedWeee = this.clearedWeeeControl.value;
    }

    return quantitative;
  }

  redirectUrl(backToList = true): void {
    let navigate = '';
    // crear redirect url
    if (backToList) {
      const url: string[] = this.router.url.split('/');
      for (let i = 0; i < url.length - 1; i++) {
        navigate = navigate.concat(url[i] + '/');
      }
      this.router.navigate([navigate], { skipLocationChange: true }).then(
        () => {
          // do nothing
        }
      );
    } else {
      this.loadData();
      this.messageService.showMessage('The data has been saved correctly', 'success');
    }
  }

  print(): void {
    this.printRegistration(this.year);
  }

  printLastYear(): void {
    this.printRegistration(this.year - 1);
  }

  private printRegistration(year: number): void {
    this.kfRegistrationService.printPdf(this.keyFigure, year,
      this.country ? this.user.weeeSystem.country.id : this.user.weeeSystem.id,
      this.country ? 'country' : 'system')
      .subscribe(response => {
        const blob = new Blob([response.body], {type: 'application/pdf'});
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      });
  }

}
